import { getAsset3DExtension } from './asset3DFormats';
import { settleWithin } from '@/utils/settleWithin';

// Babylon uses larger sensibility values for slower pointer movement.
export const ASSET_3D_VIEWER_ROTATION_SENSIBILITY = 12000;
export const ASSET_3D_VIEWER_PANNING_SENSIBILITY = 1200;
export const ASSET_3D_VIEWER_WHEEL_DELTA_PERCENTAGE = 0.003;

/**
 * How far the framing may be shifted, as a fraction of the visible half-height.
 *
 * Expressed against the viewport rather than the model because the leash has to
 * mean the same thing at every zoom level: a fixed world distance is a tight
 * leash when zoomed out (the frame covers far more world) and a slack one when
 * zoomed in. At 1.0 the model centre can travel a full half-height, so the
 * subject reaches the frame edge but never leaves it entirely.
 */
export const ASSET_3D_PAN_FRAME_FRACTION = 1.0;

export interface Asset3DViewerHandle {
	engine: any;
	scene: any;
	camera: any;
	loadedMeshes: any[];
	babylon: any;
	resizeObserver: ResizeObserver | null;
	framedRadius: number;
	dispose: () => void;
}

export interface CreateAsset3DViewerOptions {
	canvas: HTMLCanvasElement;
	file: File;
	extension?: string;
	transparentBackground?: boolean;
	backgroundColor?: string;
	cameraName?: string;
	rotationSensibility?: number;
	panningSensibility?: number;
	wheelDeltaPercentage?: number;
	attachControl?: boolean;
	runRenderLoop?: boolean;
	/** called with the viewer before its model loads: one that never loads can still be let go of */
	onBeforeLoadAsset?: (handle: Asset3DViewerHandle) => void;
}

export interface RenderAsset3DToPngOptions {
	width: number;
	height: number;
	transparentBackground?: boolean;
	backgroundColor?: string;
	// When set, the model is rendered over alpha and this image is composited
	// behind it, cover-fitted the same way the editor previews it in CSS.
	backgroundImageUrl?: string;
}

export interface CoverDrawRect {
	dx: number;
	dy: number;
	dWidth: number;
	dHeight: number;
}

export interface RenderAsset3DStaticPreviewOptions extends RenderAsset3DToPngOptions {
	extension?: string;
	/** give up after this long (ms); the viewer and its canvas go either way */
	timeoutMs?: number;
}

export async function loadBabylonAsset3DModules (): Promise<any> {
	// Deep-import only the Babylon modules this editor uses, rather than the
	// `@babylonjs/core` barrel. The barrel statically pulls in the entire engine
	// and can push webpack over the build heap for this app.
	const modules: any[] = await Promise.all([
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Engines/engine'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/scene'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Cameras/arcRotateCamera'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Lights/hemisphericLight'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Lights/directionalLight'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Maths/math.vector'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Maths/math.color'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Loading/sceneLoader'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/core/Misc/screenshotTools'),
		// Side-effect imports: register the glTF/OBJ/STL loaders with SceneLoader.
		import(/* webpackChunkName: "babylon" */ '@babylonjs/loaders/glTF'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/loaders/OBJ'),
		import(/* webpackChunkName: "babylon" */ '@babylonjs/loaders/STL'),
	]);
	const [
		{ Engine },
		{ Scene },
		{ ArcRotateCamera },
		{ HemisphericLight },
		{ DirectionalLight },
		{ Vector3 },
		{ Color4 },
		{ SceneLoader },
		{ CreateScreenshotUsingRenderTarget },
	] = modules;
	return {
		Engine,
		Scene,
		ArcRotateCamera,
		HemisphericLight,
		DirectionalLight,
		Vector3,
		Color4,
		SceneLoader,
		CreateScreenshotUsingRenderTarget,
	};
}

export async function createAsset3DViewer (options: CreateAsset3DViewerOptions): Promise<Asset3DViewerHandle> {
	const babylon = await loadBabylonAsset3DModules();
	const engine = new babylon.Engine(options.canvas, true, { preserveDrawingBuffer: true, stencil: true });
	const scene = new babylon.Scene(engine);
	const camera = new babylon.ArcRotateCamera(
		options.cameraName || 'asset3d-camera',
		Math.PI / 2,
		Math.PI / 2.5,
		3,
		babylon.Vector3.Zero(),
		scene,
	);
	if (options.attachControl !== false) camera.attachControl(options.canvas, true);
	camera.angularSensibilityX = options.rotationSensibility ?? ASSET_3D_VIEWER_ROTATION_SENSIBILITY;
	camera.angularSensibilityY = options.rotationSensibility ?? ASSET_3D_VIEWER_ROTATION_SENSIBILITY;
	camera.wheelDeltaPercentage = options.wheelDeltaPercentage ?? ASSET_3D_VIEWER_WHEEL_DELTA_PERCENTAGE;
	camera.lowerRadiusLimit = 0.01;
	camera.minZ = 0.001;
	camera.panningSensibility = options.panningSensibility ?? ASSET_3D_VIEWER_PANNING_SENSIBILITY;

	new babylon.HemisphericLight('asset3d-hemi', new babylon.Vector3(0, 1, 0), scene).intensity = 0.9;
	const dir = new babylon.DirectionalLight('asset3d-dir', new babylon.Vector3(-0.5, -1, -0.5), scene);
	dir.intensity = 0.6;

	let resizeObserver: ResizeObserver | null = null;
	if (typeof ResizeObserver !== 'undefined') {
		resizeObserver = new ResizeObserver(() => engine.resize());
		resizeObserver.observe(options.canvas);
	}

	const handle: Asset3DViewerHandle = {
		engine,
		scene,
		camera,
		loadedMeshes: [],
		babylon,
		resizeObserver,
		framedRadius: 1,
		dispose () {
			if (resizeObserver) {
				try { resizeObserver.disconnect(); } catch (_e) { /* noop */ }
				resizeObserver = null;
				handle.resizeObserver = null;
			}
			try { engine.stopRenderLoop(); } catch (_e) { /* noop */ }
			try { engine.dispose(); } catch (_e) { /* noop */ }
		},
	};

	setAsset3DViewerBackground(
		handle,
		options.transparentBackground !== false,
		options.backgroundColor || '#808080',
	);
	if (options.runRenderLoop !== false) engine.runRenderLoop(() => scene.render());

	try {
		if (options.onBeforeLoadAsset) options.onBeforeLoadAsset(handle);
		await loadAsset3DFile(handle, options.file, options.extension || getAsset3DExtension(options.file.name));
		frameAsset3DCamera(handle);
		return handle;
	} catch (err) {
		handle.dispose();
		throw err;
	}
}

export async function loadAsset3DFile (handle: Asset3DViewerHandle, file: File, extension: string): Promise<any[]> {
	const pluginExtension = extension.startsWith('.') ? extension : `.${extension}`;
	const result = await handle.babylon.SceneLoader.ImportMeshAsync(
		'',
		'',
		file,
		handle.scene,
		null,
		pluginExtension,
	);
	handle.loadedMeshes = result.meshes || [];
	return handle.loadedMeshes;
}

/**
 * Computes the world-space bounding box of everything currently loaded.
 * Returns null when nothing has a bounding box to contribute.
 */
export function getAsset3DBounds (handle: Asset3DViewerHandle): { min: any, max: any } | null {
	if (!handle.loadedMeshes.length || !handle.babylon) return null;
	const babylon = handle.babylon;
	let min: any = null;
	let max: any = null;
	for (const mesh of handle.loadedMeshes) {
		if (!mesh.getBoundingInfo) continue;
		mesh.computeWorldMatrix(true);
		const bb = mesh.getBoundingInfo().boundingBox;
		if (!min) {
			min = bb.minimumWorld.clone();
			max = bb.maximumWorld.clone();
		} else {
			min = babylon.Vector3.Minimize(min, bb.minimumWorld);
			max = babylon.Vector3.Maximize(max, bb.maximumWorld);
		}
	}
	if (!min || !max) return null;
	return { min, max };
}

/**
 * Half the world-space height the camera can see at the target plane.
 * Everything about framing pan is expressed against this so it stays
 * proportional to what is actually on screen.
 */
export function computeAsset3DViewHalfHeight (camera: any): number {
	if (!camera) return 1;
	const radius: number = Number.isFinite(camera.radius) && camera.radius > 0 ? camera.radius : 1;
	const fov: number = Number.isFinite(camera.fov) && camera.fov > 0 ? camera.fov : 0.8;
	return radius * Math.tan(fov / 2);
}

/**
 * World units travelled per pixel of cursor movement, so a drag tracks the
 * pointer 1:1 instead of needing a tuned sensibility constant. This is
 * inherently correct at any model size and any zoom, which the old
 * panningSensibility number could only approximate for one of them.
 */
export function computeAsset3DWorldPerPixel (camera: any, canvasHeightPx: number): number {
	const height: number = Number.isFinite(canvasHeightPx) && canvasHeightPx > 0 ? canvasHeightPx : 1;
	return (2 * computeAsset3DViewHalfHeight(camera)) / height;
}

/**
 * Clamps a framing offset to the leash, preserving its direction.
 * Returned as plain numbers so it is testable without a Babylon Vector2.
 */
export function clampAsset3DFramingOffset (x: number, y: number, maxDistance: number): { x: number, y: number } {
	const limit: number = Number.isFinite(maxDistance) && maxDistance > 0 ? maxDistance : 0;
	const distance: number = Math.hypot(x, y);
	if (!Number.isFinite(distance) || distance <= limit || distance === 0) {
		return { x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0 };
	}
	const scale: number = limit / distance;
	return { x: x * scale, y: y * scale };
}

export function resetAsset3DFraming (handle: Asset3DViewerHandle): void {
	if (handle.camera?.targetScreenOffset) handle.camera.targetScreenOffset.set(0, 0);
}

/**
 * Pans by shifting the framing rather than the orbit target.
 *
 * Babylon's built-in panning moves camera.target, which is also the point orbit
 * rotates about, so panning quietly drags the rotation centre off the model and
 * every later rotation feels off-axis. targetScreenOffset slides what the camera
 * shows while leaving the pivot on the model, which keeps rotation centred no
 * matter how far the view has been panned.
 */
export function attachAsset3DFramingPan (handle: Asset3DViewerHandle, canvas: HTMLCanvasElement): () => void {
	const camera = handle.camera;
	if (!camera || !canvas) return () => { /* noop */ };

	// Neutralise Babylon's own panning without letting the gesture fall through
	// to rotation. ArcRotateCameraPointersInput branches on
	// `panningSensibility !== 0 && (ctrlKey || isPanClick)`, so zeroing the
	// sensibility does not disable panning: it fails the test and rotates
	// instead. Keeping the sensibility non-zero holds the gesture in the pan
	// branch, and a zeroed panningAxis makes that branch move nothing, because
	// _checkInputs multiplies the pan direction by it.
	camera.panningSensibility = ASSET_3D_VIEWER_PANNING_SENSIBILITY;
	camera.panningAxis = new handle.babylon.Vector3(0, 0, 0);

	let panning: boolean = false;
	let lastX: number = 0;
	let lastY: number = 0;

	const isPanGesture = (ev: PointerEvent): boolean => ev.button === 2 || (ev.button === 0 && ev.ctrlKey);

	const onPointerDown = (ev: PointerEvent): void => {
		if (!isPanGesture(ev)) return;
		panning = true;
		lastX = ev.clientX;
		lastY = ev.clientY;
		canvas.setPointerCapture?.(ev.pointerId);
		ev.preventDefault();
	};

	const onPointerMove = (ev: PointerEvent): void => {
		if (!panning) return;
		// setPointerCapture is optional-chained above, so where capture is
		// unavailable a release outside the canvas never reaches pointerup and the
		// model would follow the bare cursor. buttons is the authoritative state.
		if (ev.buttons === 0) {
			panning = false;
			return;
		}
		const dx: number = ev.clientX - lastX;
		const dy: number = ev.clientY - lastY;
		lastX = ev.clientX;
		lastY = ev.clientY;

		const worldPerPixel: number = computeAsset3DWorldPerPixel(camera, canvas.clientHeight);
		// Signed so the model tracks the cursor, as though the drag has hold of the
		// object itself. targetScreenOffset shifts the camera's view, so following
		// the pointer means moving the offset with it on x and against it on y,
		// screen y running opposite to world up.
		const nextX: number = camera.targetScreenOffset.x + (dx * worldPerPixel);
		const nextY: number = camera.targetScreenOffset.y - (dy * worldPerPixel);

		const limit: number = computeAsset3DViewHalfHeight(camera) * ASSET_3D_PAN_FRAME_FRACTION;
		const clamped = clampAsset3DFramingOffset(nextX, nextY, limit);
		camera.targetScreenOffset.set(clamped.x, clamped.y);
		ev.preventDefault();
	};

	const stopPanning = (ev: PointerEvent): void => {
		if (!panning) return;
		panning = false;
		canvas.releasePointerCapture?.(ev.pointerId);
	};

	// Zooming shrinks the leash, so an offset that was legal before can end up
	// outside it. Re-clamping each frame keeps the model from drifting out of
	// frame on zoom-in without the user having touched pan.
	const observer = handle.scene?.onBeforeRenderObservable?.add(() => {
		if (!camera.targetScreenOffset) return;
		const limit: number = computeAsset3DViewHalfHeight(camera) * ASSET_3D_PAN_FRAME_FRACTION;
		const clamped = clampAsset3DFramingOffset(camera.targetScreenOffset.x, camera.targetScreenOffset.y, limit);
		camera.targetScreenOffset.set(clamped.x, clamped.y);
	});

	const onContextMenu = (ev: Event): void => ev.preventDefault();

	canvas.addEventListener('pointerdown', onPointerDown);
	canvas.addEventListener('pointermove', onPointerMove);
	canvas.addEventListener('pointerup', stopPanning);
	canvas.addEventListener('pointercancel', stopPanning);
	canvas.addEventListener('contextmenu', onContextMenu);

	return () => {
		canvas.removeEventListener('pointerdown', onPointerDown);
		canvas.removeEventListener('pointermove', onPointerMove);
		canvas.removeEventListener('pointerup', stopPanning);
		canvas.removeEventListener('pointercancel', stopPanning);
		canvas.removeEventListener('contextmenu', onContextMenu);
		if (observer && handle.scene?.onBeforeRenderObservable) {
			handle.scene.onBeforeRenderObservable.remove(observer);
		}
	};
}

export function frameAsset3DCamera (handle: Asset3DViewerHandle): void {
	if (!handle.camera || !handle.babylon) return;
	const bounds = getAsset3DBounds(handle);
	if (!bounds) return;

	const center = bounds.min.add(bounds.max).scale(0.5);
	const size = bounds.max.subtract(bounds.min);
	const radius = Math.max(size.x, size.y, size.z) * 1.6 || 2;

	handle.camera.setTarget(center);
	handle.camera.radius = radius;
	handle.camera.lowerRadiusLimit = radius * 0.1;
	handle.camera.upperRadiusLimit = radius * 10;
	handle.camera.alpha = Math.PI / 2;
	handle.camera.beta = Math.PI / 2.5;

	handle.framedRadius = radius;
	// Re-framing means re-centring: a leftover offset would put the model
	// off-centre immediately after a reset.
	resetAsset3DFraming(handle);
}

export function setAsset3DViewerBackground (
	handle: Asset3DViewerHandle,
	transparentBackground: boolean,
	backgroundColor: string,
): void {
	if (!handle.scene || !handle.babylon) return;
	if (transparentBackground) {
		handle.scene.clearColor = new handle.babylon.Color4(0, 0, 0, 0);
		return;
	}
	const { r, g, b } = hexToRgb01(backgroundColor);
	handle.scene.clearColor = new handle.babylon.Color4(r, g, b, 1);
}

export async function renderAsset3DViewerToPngBlob (
	handle: Asset3DViewerHandle,
	options: RenderAsset3DToPngOptions,
): Promise<Blob> {
	const dataUrl = await renderAsset3DViewerToPngDataUrl(handle, options);
	if (options.backgroundImageUrl) {
		return compositePngDataUrlOnBackgroundImage(dataUrl, options.backgroundImageUrl);
	}
	if (options.transparentBackground !== false) {
		const res = await fetch(dataUrl);
		return res.blob();
	}
	return compositePngDataUrlOnBackground(dataUrl, options.backgroundColor || '#808080');
}

export function renderAsset3DViewerToPngDataUrl (
	handle: Asset3DViewerHandle,
	options: RenderAsset3DToPngOptions,
): Promise<string> {
	return new Promise<string>((resolve) => {
		handle.babylon.CreateScreenshotUsingRenderTarget(
			handle.engine,
			handle.camera,
			{ width: options.width, height: options.height, precision: 1 },
			(url: string) => resolve(url),
			'image/png',
			1,
			true,
		);
	});
}

export function compositePngDataUrlOnBackground (dataUrl: string, hexColor: string): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Canvas 2D context unavailable'));
				return;
			}
			ctx.fillStyle = hexColor;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, 0, 0);
			canvas.toBlob((blob) => {
				if (blob) resolve(blob);
				else reject(new Error('Failed to encode captured PNG'));
			}, 'image/png');
		};
		img.onerror = () => reject(new Error('Failed to load captured screenshot'));
		img.src = dataUrl;
	});
}

/**
 * Geometry for CSS `background-size: cover` + `background-position: center`.
 *
 * The editor previews an image background by handing those two CSS properties a
 * square wrapper, and the capture has to reproduce the identical framing or the
 * uploaded PNG would not match what the user posed against. Keeping the math in
 * one pure function is what ties the two paths together.
 */
export function computeCoverDrawRect (
	sourceWidth: number,
	sourceHeight: number,
	targetWidth: number,
	targetHeight: number,
): CoverDrawRect {
	// A source that reports no usable dimensions cannot be scaled by ratio.
	// Filling the target keeps the capture opaque instead of dividing by zero.
	if (!sourceWidth || !sourceHeight) {
		return { dx: 0, dy: 0, dWidth: targetWidth, dHeight: targetHeight };
	}

	const scale = Math.max(targetWidth / sourceWidth, targetHeight / sourceHeight);
	const dWidth = sourceWidth * scale;
	const dHeight = sourceHeight * scale;

	return {
		dx: (targetWidth - dWidth) / 2,
		dy: (targetHeight - dHeight) / 2,
		dWidth,
		dHeight,
	};
}

export function compositePngDataUrlOnBackgroundImage (dataUrl: string, backgroundImageUrl: string): Promise<Blob> {
	return new Promise((resolve, reject) => {
		// Image callbacks run outside this executor's call stack, so anything they
		// throw (toBlob raises SecurityError on a canvas the browser considers
		// tainted) would escape instead of rejecting, and the capture would await a
		// promise that never settles. Every callback body goes through `settle`.
		const settle = (body: () => void) => {
			try {
				body();
			} catch (err: any) {
				reject(err instanceof Error ? err : new Error('Failed to composite the background image'));
			}
		};

		const bgImg = new Image();
		// Without this the browser taints the canvas on a cross-origin background
		// and toBlob throws a SecurityError instead of encoding. With it, an image
		// served without CORS approval fails in onerror, which is recoverable.
		bgImg.crossOrigin = 'anonymous';
		bgImg.onload = () => settle(() => {
			const modelImg = new Image();
			modelImg.onload = () => settle(() => {
				const canvas = document.createElement('canvas');
				canvas.width = modelImg.width;
				canvas.height = modelImg.height;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					reject(new Error('Canvas 2D context unavailable'));
					return;
				}
				const { dx, dy, dWidth, dHeight } = computeCoverDrawRect(
					bgImg.naturalWidth || bgImg.width,
					bgImg.naturalHeight || bgImg.height,
					canvas.width,
					canvas.height,
				);
				ctx.drawImage(bgImg, dx, dy, dWidth, dHeight);
				ctx.drawImage(modelImg, 0, 0);
				canvas.toBlob((blob) => settle(() => {
					if (blob) resolve(blob);
					else reject(new Error('Failed to encode captured PNG'));
				}), 'image/png');
			});
			modelImg.onerror = () => reject(new Error('Failed to load captured screenshot'));
			modelImg.src = dataUrl;
		});
		bgImg.onerror = () => reject(new Error('Failed to load the background image'));
		bgImg.src = backgroundImageUrl;
	});
}

export function hexToRgb01 (hex: string): { r: number, g: number, b: number } {
	const h = (hex || '#000000').replace('#', '');
	const r = parseInt(h.substring(0, 2), 16) / 255;
	const g = parseInt(h.substring(2, 4), 16) / 255;
	const b = parseInt(h.substring(4, 6), 16) / 255;
	return { r, g, b };
}

export async function renderAsset3DStaticPreviewBlob (
	file: File,
	options: RenderAsset3DStaticPreviewOptions,
): Promise<Blob> {
	const canvas = document.createElement('canvas');
	canvas.width = options.width;
	canvas.height = options.height;
	canvas.style.position = 'fixed';
	canvas.style.left = '-10000px';
	canvas.style.top = '-10000px';
	canvas.style.width = `${options.width}px`;
	canvas.style.height = `${options.height}px`;
	document.body.appendChild(canvas);

	const made: { handle: Asset3DViewerHandle | null, frames: ReturnType<typeof setInterval> | null, over: boolean } = { handle: null, frames: null, over: false };
	const transparentBackground = options.transparentBackground === true;
	const backgroundColor = options.backgroundColor || '#1a1a1a';
	const render = async (): Promise<Blob> => {
		const handle = await createAsset3DViewer({
			canvas,
			file,
			extension: options.extension || getAsset3DExtension(file.name),
			transparentBackground,
			backgroundColor,
			attachControl: false,
			runRenderLoop: false,
			// held from the start, so giving up while the model still loads lets go of the viewer too
			onBeforeLoadAsset: (h) => { made.handle = h; },
		});
		// given up while the model loaded: nothing is waiting for this viewer
		if (made.over) {
			handle.dispose();
			throw new Error('The 3D preview was given up');
		}
		made.handle = handle;
		// Babylon's render-target screenshot calls back once a frame ends, and only a frame's end
		// does it: one scene drawn with no render loop never called back, so every 3D upload waited
		// on its preview for good (P3-13). Frames on a timer end it, in a tab in the background too.
		made.frames = setInterval(() => {
			handle.engine.beginFrame();
			handle.scene.render();
			handle.engine.endFrame();
		}, 16);
		return renderAsset3DViewerToPngBlob(handle, {
			width: options.width,
			height: options.height,
			transparentBackground,
			backgroundColor,
		});
	};
	try {
		return await (options.timeoutMs ? settleWithin(render(), options.timeoutMs, 'The 3D preview') : render());
	} finally {
		made.over = true;
		if (made.frames) clearInterval(made.frames);
		if (made.handle) made.handle.dispose();
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	}
}
